import { useState, useRef, useCallback } from "react";

// ─── Agent SSE Event Types ──────────────────────────────────────────────────

export interface AgentEvent {
  type: "started" | "plan" | "thinking" | "thought" | "token" | "tool_start" | "tool_result" | "self_correction" | "final_answer" | "content" | "reflection" | "done" | "error";
  step?: number;
  totalSteps?: number;
  taskId?: string;
  // plan (raw string from backend)
  plan?: string;
  // thinking / thought
  thought?: string;
  action?: string;
  // token (streaming)
  content?: string;
  // tool_start / tool_result
  tool?: string;
  connector?: string;
  args?: Record<string, unknown>;
  status?: string;
  preview?: string;
  error?: string;
  executionTime?: number;
  // self_correction
  reason?: string;
  errorPreview?: string;
  // final_answer / done
  answer?: string;
  finalAnswer?: string;
  model?: string;
  latencyMs?: number;
  executionTimeMs?: number;
  tokens?: number;
  // error
  message?: string;
  timestamp: number;
}

interface UseAgentSSEOptions {
  onEvent?: (event: AgentEvent) => void;
  onDone?: (finalAnswer: string, events: AgentEvent[]) => void;
  onError?: (error: string) => void;
}

function getApiBaseUrl(): string {
  const envUrl = (import.meta as any).env?.VITE_API_URL;
  if (envUrl) return envUrl;
  return ""; // same origin in dev
}

/**
 * Get a fresh access token, refreshing if needed.
 */
async function getFreshToken(): Promise<string | null> {
  const token = localStorage.getItem("accessToken");
  if (!token) return null;

  // Check if token is about to expire (decode JWT payload)
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expiresIn = (payload.exp * 1000) - Date.now();
    if (expiresIn > 60000) return token; // More than 60s left, token is fine
  } catch {
    return token; // Can't decode, try using it anyway
  }

  // Token is about to expire, try to refresh
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return token;

  try {
    const baseUrl = getApiBaseUrl();
    const refreshResponse = await fetch(`${baseUrl}/trpc/auth.refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ json: { refreshToken } }),
    });

    if (!refreshResponse.ok) return token;

    const data = await refreshResponse.json();
    const result = data?.result?.data?.json;
    if (result?.accessToken && result?.refreshToken) {
      localStorage.setItem("accessToken", result.accessToken);
      localStorage.setItem("refreshToken", result.refreshToken);
      return result.accessToken;
    }
  } catch {
    // Refresh failed, try with current token
  }

  return token;
}

/**
 * Normalize raw backend SSE data into a consistent AgentEvent.
 * The backend emits slightly different field names than what the UI expects.
 */
function normalizeEvent(raw: Record<string, any>): AgentEvent {
  const event: AgentEvent = {
    ...raw,
    type: raw.type || "unknown",
    timestamp: Date.now(),
  };

  // Normalize done event fields
  if (raw.type === "done") {
    event.finalAnswer = raw.answer || raw.finalAnswer || "";
    event.latencyMs = raw.execution_time_ms || raw.latencyMs;
    event.totalSteps = raw.total_steps || raw.totalSteps;
  }

  // Normalize self_correction fields
  if (raw.type === "self_correction") {
    event.reason = raw.reason || "";
    event.errorPreview = raw.error_preview || "";
  }

  // Normalize token fields
  if (raw.type === "token") {
    event.content = raw.content || "";
  }

  // Normalize content event (from chat/tools/stream)
  if (raw.type === "content") {
    event.content = raw.content || "";
    // Also map to answer for compatibility with AgentStepInline
    event.answer = raw.content || "";
  }

  // Normalize final_answer event
  if (raw.type === "final_answer") {
    event.answer = raw.answer || raw.content || "";
  }

  return event;
}

export function useAgentSSE(options: UseAgentSSEOptions = {}) {
  const [events, setEvents] = useState<AgentEvent[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamingText, setStreamingText] = useState<string>("");
  const abortRef = useRef<AbortController | null>(null);
  const streamingBufferRef = useRef<string>("");
  // Keep a ref to current events for snapshot in onDone callback
  const eventsRef = useRef<AgentEvent[]>([]);

  const clearEvents = useCallback(() => {
    setEvents([]);
    eventsRef.current = [];
    setError(null);
    setStreamingText("");
    streamingBufferRef.current = "";
  }, []);

  const stop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsRunning(false);
  }, []);

  const execute = useCallback(async (params: {
    goal: string;
    connectors?: string[];
    maxSteps?: number;
    model?: string;
    history?: Array<{ role: string; content: string }>;
  }) => {
    // Reset state for NEW execution
    setEvents([]);
    eventsRef.current = [];
    setError(null);
    setStreamingText("");
    streamingBufferRef.current = "";
    setIsRunning(true);

    // Get fresh token with auto-refresh
    const token = await getFreshToken();
    if (!token) {
      const msg = "Autenticação necessária. Faça login novamente.";
      setError(msg);
      setIsRunning(false);
      options.onError?.(msg);
      return;
    }

    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/sse/agent/execute?token=${encodeURIComponent(token)}`;

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => "Unknown error");
        if (response.status === 401) {
          throw new Error("Sessão expirada. Faça login novamente.");
        }
        throw new Error(`Erro do servidor (${response.status}): ${errText}`);
      }

      if (!response.body) {
        throw new Error("Servidor não retornou stream de dados.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(line.slice(6));
            const event = normalizeEvent(data);

            // Handle token streaming — accumulate without adding to events array
            if (event.type === "token") {
              streamingBufferRef.current += event.content || "";
              setStreamingText(streamingBufferRef.current);
              options.onEvent?.(event);
              continue; // Don't add individual tokens to events
            }

            // When a thought arrives after tokens, reset streaming buffer
            if (event.type === "thought" || event.type === "thinking") {
              streamingBufferRef.current = "";
              setStreamingText("");
            }

            setEvents((prev) => {
              const updated = [...prev, event];
              eventsRef.current = updated;
              return updated;
            });
            options.onEvent?.(event);

            if (event.type === "done") {
              // Pass the complete events snapshot to onDone
              // Try multiple sources for the final answer
              let finalAnswer = event.finalAnswer || event.answer || "";
              if (!finalAnswer) {
                // Fallback: look for final_answer or content events in the events list
                const allEvents = eventsRef.current;
                const faEvent = allEvents.find((e) => e.type === "final_answer");
                const cEvent = allEvents.find((e) => e.type === "content");
                finalAnswer = faEvent?.answer || cEvent?.content || cEvent?.answer || "";
              }
              options.onDone?.(finalAnswer, [...eventsRef.current]);
            }
            if (event.type === "error") {
              setError(event.message || "Erro desconhecido");
              options.onError?.(event.message || "Erro desconhecido");
            }
          } catch {
            // Skip malformed SSE lines
          }
        }
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        let msg = err.message || "Falha na conexão";
        // Provide more descriptive error messages
        if (msg === "fetch failed" || msg === "Failed to fetch") {
          msg = "Não foi possível conectar ao servidor. Verifique sua conexão ou tente novamente.";
        }
        setError(msg);
        options.onError?.(msg);
      }
    } finally {
      setIsRunning(false);
      abortRef.current = null;
    }
  }, [options]);

  return {
    events,
    isRunning,
    error,
    streamingText,
    execute,
    stop,
    clearEvents,
  };
}
